<script lang="ts" setup>
	import { useForm } from 'ant-design-vue/es/form'

	export type PolicyActivationSteps = { key: string; title?: string; subtitle?: string }[]
	const props = defineProps<{
		show: boolean
		steps: PolicyActivationSteps
		activating?: boolean
		formData?: unknown
		intercept?: () => Promise<boolean>
	}>()

	const { resetFields } = useForm(props.formData)

	const emits = defineEmits<{
		(event: 'update:show', param: boolean): void
		(event: 'onStepChange', param: PolicyActivationSteps[number]): void
		(event: 'done'): void
	}>()
	const currentStep = shallowRef(0)
	const formEl = ref<HTMLFormElement | null>(null)

	const header = computed(() => (Array.isArray(props.steps) ? props.steps[currentStep.value] : null))
	const validate = async (arg?: Function) => {
		if (formEl.value) {
			try {
				await formEl.value.validate()
				if (arg) {
					arg()
				}
				return Promise.resolve(true)
			} catch (error) {
				console.error(error)
			}
		}
	}

	const nextStep = () => {
		validate().then(async () => {
			if (currentStep.value < props.steps.length - 1) {
				if (props.intercept && currentStep.value === 0) {
					const valid = await props.intercept()

					if (valid) {
						currentStep.value++
						emits('onStepChange', props.steps[currentStep.value])
					}
				} else {
					currentStep.value++
					emits('onStepChange', props.steps[currentStep.value])
				}
			}

			emits('done')
		})
	}
	const prevStep = () => {
		if (currentStep.value !== 0) {
			currentStep.value--
			emits('onStepChange', props.steps[currentStep.value])
		}
	}
	const resetForm = () => {
		resetFields()
		currentStep.value = 0
	}
</script>

<template>
	<dialogs
		:show="show"
		:title="header?.title"
		:paragraph="header?.subtitle"
		modal-class="!py-8 max-w-[626px]"
		:ui="{
			modal: 'px-3 md:px-8'
		}"
		@update:show="resetForm"
		@change="$emit('update:show', false)"
	>
		<slot :current-step="steps[currentStep]?.key || ''" />
		<a-form
			ref="formEl"
			:model="formData"
			:validate-messages="antValidateMessages"
			:colon="false"
			@finish="nextStep"
		>
			<slot
				:name="steps[currentStep]?.key || currentStep"
				:next-step="nextStep"
				:prev-step="prevStep"
				:reset="resetForm"
				:validate="validate"
				:done="(arg: Function) => validate(arg($event, resetFields))"
			/>
		</a-form>
	</dialogs>
</template>
